// ============================================================================
// seedResolver — pre-fills grid rows from a runtime context (e.g. case parties)
// ============================================================================
// Mirrors optionsResolver, but for the GridFieldSpec.seed_from hook. The
// resolver receives an opaque context bag from DynamicForm (caseId, etc.)
// and returns row values to append to the grid.
//
// Why a separate registry from optionsResolver?
//   • Options resolvers are stateless lookups (load a master list).
//   • Seed resolvers need runtime context (this case's parties).
//   • Keeping them apart avoids forcing every options resolver to accept a
//     context arg it doesn't care about.

import type { GridRowValues } from 'api/types/formSpec.types';

export type SeedContext = Record<string, unknown>;
export type SeedResolver = (ctx: SeedContext) => Promise<GridRowValues[]>;

const registry = new Map<string, SeedResolver>();

export function registerSeedResolver(source: string, resolver: SeedResolver): void {
  registry.set(source, resolver);
}

export async function resolveSeed(
  source: string,
  ctx: SeedContext,
): Promise<GridRowValues[]> {
  const resolver = registry.get(source);
  if (!resolver) {
    // eslint-disable-next-line no-console
    console.warn(`[seedResolver] no resolver registered for "${source}" — returning []`);
    return [];
  }
  try {
    return await resolver(ctx);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`[seedResolver] resolver "${source}" failed:`, err);
    return [];
  }
}

export function hasSeedResolver(source: string): boolean {
  return registry.has(source);
}
